import React, { useEffect, useRef, useState } from 'react';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';

interface GridLabelProps {
  areaName: string;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

const GridLabel: React.FC<GridLabelProps> = ({ areaName, position = 'top-right' }) => {
  const { showGridLabels } = useDisplayPreferences();
  const parentRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!showGridLabels || !parentRef.current) return;

    const updateDimensions = () => {
      const parent = parentRef.current?.parentElement;
      if (parent) {
        const rect = parent.getBoundingClientRect();
        setDimensions({
          width: Math.round(rect.width),
          height: Math.round(rect.height)
        });
      }
    };

    // Initial measurement
    updateDimensions();

    // Watch for resize
    const resizeObserver = new ResizeObserver(updateDimensions);
    const parent = parentRef.current.parentElement;
    if (parent) {
      resizeObserver.observe(parent);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [showGridLabels]);

  if (!showGridLabels) return null;

  const positionStyles: Record<string, React.CSSProperties> = {
    'top-left': { top: 4, left: 4 },
    'top-right': { top: 4, right: 4 },
    'bottom-left': { bottom: 4, left: 4 },
    'bottom-right': { bottom: 4, right: 4 },
  };

  return (
    <div
      ref={parentRef}
      style={{
        position: 'absolute',
        ...positionStyles[position],
        padding: '2px 6px',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
        fontSize: '10px',
        fontWeight: 'bold',
        borderRadius: '3px',
        pointerEvents: 'none',
        zIndex: 99999,
        whiteSpace: 'nowrap',
      }}
    >
      {areaName} ({dimensions.width}×{dimensions.height})
    </div>
  );
};

export default GridLabel;
